import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import Lightbox from '../shared/Lightbox';
import Img from '@/components/shared/Img';

const projects = [
  {
    title: 'Master Bathroom Transformation',
    summary: 'A complete gut-and-rebuild of an outdated master bath — custom tile shower, new vanity, frameless glass enclosure, and full plumbing updates.',
    type: 'Bathroom Remodel',
    location: 'Rockport, TX',
    image: '/images/9365c7713_Bathroom.jpg',
    alt: 'Master bathroom remodel Rockport Texas by Terza Costa Construction',
    link: '/gallery',
  },
  {
    title: 'Modern Kitchen Remodel',
    summary: 'Full kitchen renovation including new layout, custom cabinetry, quartz countertops, backsplash, and updated lighting throughout.',
    type: 'Kitchen Remodel',
    location: 'Corpus Christi, TX',
    image: '/images/2a4ac8731_Kitchen.jpg',
    alt: 'Modern kitchen remodel Corpus Christi Coastal Bend',
    link: '/gallery',
  },
  {
    title: 'Custom Entertainment Center',
    summary: 'A blank wall transformed into a fully custom built-in — floating wood console, integrated linear fireplace, and a black canopy with side shelving, designed to fit the room perfectly.',
    type: 'Custom Built-In',
    location: 'Coastal Bend, TX',
    image: '/images/tv-entertainment-center-finished.jpg',
    alt: 'Custom built-in entertainment center with linear electric fireplace and floating wood console — Terza Costa Construction',
    link: '/gallery#entertainment-center',
  },
];

export default function FeaturedProjects() {
  const [lightbox, setLightbox] = useState(null);
  const projectImages = projects.map(p => ({ src: p.image, alt: p.alt }));

  return (
    <>
    <section className="py-20 md:py-28 bg-[#111111] border-t border-white/10">
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          <div className="h-px w-16 bg-[#C97B2E] mx-auto mb-6" />
          <span className="font-body text-[#C97B2E] text-xs font-bold uppercase tracking-[0.3em] mb-4 block">
            Real Projects
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Featured Work
          </h2>
          <p className="font-body text-white/55 text-base md:text-lg max-w-xl mx-auto">
            A look at some of the projects we're most proud of — each one a reflection of the 
            detail, communication, and craft we bring to every job.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
          {projects.map((p, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
              className="group bg-[#1a1a1a] border border-white/10 rounded-sm overflow-hidden hover:shadow-xl hover:border-[#C97B2E]/30 transition-all duration-500"
            >
              <div
                className="overflow-hidden h-64 relative cursor-pointer"
                onClick={() => setLightbox(i)}
              >
                <Img
                  src={p.image}
                  alt={p.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  sizes="(min-width: 1280px) 400px, (min-width: 1024px) 31vw, (min-width: 640px) 46vw, calc(100vw - 2.5rem)"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-[#C97B2E] text-[#0a0a0a] px-3 py-1 rounded-sm text-xs font-body font-bold uppercase tracking-wider opacity-90">
                    {p.type}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <p className="font-body text-[#C97B2E] text-xs uppercase tracking-wider mb-2">{p.location}</p>
                <h3 className="font-heading text-white text-xl font-bold mb-3">{p.title}</h3>
                <p className="font-body text-white/55 text-sm leading-relaxed mb-5">{p.summary}</p>
                <Link
                  to={p.link}
                  className="inline-flex items-center gap-2 font-body text-[#C97B2E] text-xs font-bold uppercase tracking-wider hover:text-white transition-colors group/link"
                >
                  {p.link.includes('#') ? 'View Project' : 'View More Work'}
                  <span className="sr-only">: {p.title}</span>
                  <ArrowRight className="w-3 h-3 group-hover/link:translate-x-1 transition-transform" aria-hidden="true" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-12"
        >
          <Link
            to="/gallery"
            className="inline-flex items-center gap-3 border-2 border-white/20 text-white px-8 py-4 rounded-sm font-body font-bold uppercase tracking-wider text-sm hover:border-[#C97B2E] hover:text-[#C97B2E] transition-all duration-300 group"
          >
            See All Our Work
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>

    {lightbox !== null && (
      <Lightbox
        images={projectImages}
        startIndex={lightbox}
        onClose={() => setLightbox(null)}
      />
    )}
  </>
  );
}